import React, { useState, useEffect } from 'react';
import RedonaIMG from '../../assets/images/Redona-screen.png';
import './RedonaAI.css';

const RedonaShowcase = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [particles, setParticles] = useState([]);
  const [glowPosition, setGlowPosition] = useState({ x: 50, y: 50 });

  // Initialize particles
  useEffect(() => {
    const newParticles = Array.from({ length: 15 }, () => ({
      id: Math.random(),
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      speed: Math.random() * 0.2 + 0.1,
      direction: Math.random() * 360
    }));
    setParticles(newParticles);

    const interval = setInterval(() => {
      setParticles(prev =>
        prev.map(p => {
          const newX = (p.x + Math.cos(p.direction * Math.PI / 180) * p.speed) % 100;
          const newY = (p.y + Math.sin(p.direction * Math.PI / 180) * p.speed) % 100;
          return {
            ...p,
            x: newX < 0 ? newX + 100 : newX,
            y: newY < 0 ? newY + 100 : newY
          };
        })
      );
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Track mouse for glow effect
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setGlowPosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100
    });
  };

  const windowLocationChange = () => {
    const url = 'https://redona.ai';
    window.open(url, '_blank');
  };

  const features = {
    analytics: {
      title: "Predictive Analytics",
      description: "Redona's neural networks forecast market trends with 94% accuracy",
      stats: [
        { value: "3.5x", label: "Faster insights" },
        { value: "28%", label: "Higher accuracy" }
      ],
      visualization: (
        <div className="data-visualization">
          <div className="network-visual">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="network-node" style={{
                left: `${20 + (i % 4) * 20}%`,
                top: `${10 + Math.floor(i / 4) * 30}%`
              }}></div>
            ))}
          </div>
        </div>
      )
    },
    automation: {
      title: "Process Automation",
      description: "AI-driven workflow optimization reduces operational costs",
      stats: [
        { value: "80%", label: "Tasks automated" },
        { value: "42%", label: "Cost reduction" }
      ],
      visualization: (
        <div className="automation-visual">
          <div className="gear large"></div>
          <div className="gear medium"></div>
          <div className="gear small"></div>
        </div>
      )
    },
    integration: {
      title: "Enterprise Integration",
      description: "Seamless connection with all your business systems",
      stats: [
        { value: "9s", label: "Data sync" },
        { value: "24/7", label: "Uptime" }
      ],
      visualization: (
        <div className="integration-visual">
          <div className="hub"></div>
          <div className="connection"></div>
          <div className="connection"></div>
          <div className="connection"></div>
        </div>
      )
    }
  };

  return (
    <div className="redona-showcase" onMouseMove={handleMouseMove}>
      {/* Floating particles */}
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            opacity: p.size / 6
          }}
        />
      ))}

      {/* Dynamic glow */}
      <div
        className="glow-effect"
        style={{
          background: `radial-gradient(circle at ${glowPosition.x}% ${glowPosition.y}%, rgba(56, 182, 255, 0.2), transparent 70%)`
        }}
      />

      {/* Hero section */}
      <div className="hero-section1">
        <div className="hero-content">
          <div className="tech-label">ENTERPRISE AI PLATFORM</div>
          <h1>
            <span className="gradient-text">Redona</span> Business Intelligence
          </h1>
          <p className="hero-description">
            The most advanced AI solution for enterprise decision-making and operational excellence
          </p>
          <div className="hero-stats">
            <div className="stat"><div className="stat-value">94%</div><div className="stat-label-redona">Accuracy</div></div>
            <div className="stat"><div className="stat-value">3.2x</div><div className="stat-label-redona">ROI</div></div>
            <div className="stat"><div className="stat-value">∞</div><div className="stat-label-redona">Scalability</div></div>
          </div>
        </div>
        <div className="hero-visual">
          <img src={RedonaIMG} alt="Redona Interface" className="dashboard-image" />
          <div className="screen-glow"></div>
        </div>
      </div>

      {/* Feature tabs */}
      <div className="feature-tabs">
        {Object.keys(features).map(key => (
          <button
            key={key}
            className={`tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {features[key].title}
          </button>
        ))}
      </div>

      {/* Feature display */}
      <div className="feature-display">
        <div className="feature-info">
          <h2>{features[activeTab].title}</h2>
          <p>{features[activeTab].description}</p>
          <div className="feature-stats">
            {features[activeTab].stats.map((stat, i) => (
              <div key={i} className="feature-stat">
                <div className="feature-stat-value">{stat.value}</div>
                <div className="feature-stat-label-redona">{stat.label}</div>
              </div>
            ))}
          </div>
          <button className="cta-button" onClick={() =>windowLocationChange()}>
            Learn More <span className="arrow">→</span>
          </button>
        </div>
        <div className="feature-visual">{features[activeTab].visualization}</div>
      </div>
    </div>
  );
};

export default RedonaShowcase;
